import { create } from 'zustand';
import { 
  Post,
  fetchPosts,
  fetchCategories,
  fetchTags,
  searchPosts,
  viewPost,
  createPost as apiCreatePost
} from '../api/posts';

// Reuse Post interface from posts.ts, add optional content field for store
interface StorePost extends Post {
  content?: string;
}

interface Category {
  name: string;
  count: number;
}

interface Tag {
  name: string;
  count: number;
}

interface PostState {
  posts: StorePost[];
  randomPosts: StorePost[];
  categories: Category[];
  tags: Tag[];
  searchResults: StorePost[];
  searchTotal: number;
  searchTotalPages: number;
  loading: boolean;
  error: string | null;
}

interface PostActions {
  fetchPosts: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchTags: () => Promise<void>;
  selectRandomPosts: () => void;
  checkPostAccess: (postId: string, flag?: string) => Promise<string>;
  createPost: (formData: FormData) => Promise<void>;
  searchPosts: (params: {
    title?: string;
    platform?: string;
    category?: string;
    tags?: string[];
    page?: number;
    pageSize?: number;
    sort?: 'title' | 'updatedAt' | 'createdAt';
    order?: 'asc' | 'desc';
  }) => Promise<StorePost[]>;
}

type PostStore = PostState & PostActions;

export const usePostStore = create<PostStore>((set, get) => ({
  posts: [],
  randomPosts: [],
  categories: [],
  tags: [],
  searchResults: [],
  searchTotal: 0,
  searchTotalPages: 1,
  loading: false,
  error: null,

  fetchPosts: async () => {
    set({ loading: true, error: null });
    try {
      const posts = await fetchPosts();
      set({ posts, loading: false });
      get().selectRandomPosts();
    } catch (error) {
      set({ error: 'Failed to fetch posts', loading: false });
      console.error('Error fetching posts:', error);
    }
  },

  fetchCategories: async () => {
    try {
      const categoryNames = await fetchCategories();
      // Convert string array to Category objects with count: 0
      const categories = categoryNames.map((name: string) => ({ name, count: 0 }));
      set({ categories });
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  },

  fetchTags: async () => {
    try {
      const tagData = await fetchTags();
      // Convert API response to store format
      const tags = tagData.map((item) => ({ name: item.tag, count: item.count }));
      set({ tags });
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  },

  selectRandomPosts: () => {
    const { posts } = get();
    if (posts.length > 0) {
      const shuffled = [...posts].sort(() => 0.5 - Math.random());
      set({ randomPosts: shuffled.slice(0, 5) });
    }
  },

  checkPostAccess: async (postId: string, flag?: string) => {
    const response = await viewPost(postId, flag);
    return response.filedata;
  },
  
  createPost: async (formData: FormData) => {
    try {
      await apiCreatePost(formData);
      // Refresh posts after creation
      get().fetchPosts();
    } catch (error) {
      throw new Error('Failed to create post');
    }
  },

  searchPosts: async (params: {
    title?: string;
    platform?: string;
    category?: string;
    tags?: string[];
    page?: number;
    pageSize?: number;
    sort?: 'title' | 'updatedAt' | 'createdAt';
    order?: 'asc' | 'desc';
  }) => {
    set({ loading: true, error: null });
    try {
      const response = await searchPosts(params);
      set({ searchResults: response.posts || [], searchTotal: response.count || 0, searchTotalPages: response.totalPages || 1, loading: false });
      return response.posts || [];
    } catch (error) {
      set({ error: 'Failed to search posts', searchResults: [], searchTotal: 0, searchTotalPages: 1, loading: false });
      console.error('Error searching posts:', error);
      return [];
    }
  },
  

}
))
;
